import { Option } from "api/types/_public";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownOption from "components/Common/DropdownOption";
import LabelWrapper from "components/Common/LabelWrapper";
import TableAcquisition from "components/Common/TableAcquisition";
import { useRole } from "components/Hooks/UserHooks";
import en from 'date-fns/locale/en-US';
import ko from 'date-fns/locale/ko';
import { APP_TYPE_OPTIONS, ARR_INDEX_TEXT_DAY_OF_WEEK } from "helpers/constans";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { createSelector } from "reselect";
import { NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import { getAcquisitionStatistics as onGetStatistics } from "../../../store/thunks";
import ChartLine from "./ChartLine";
import TableContainer from "components/Common/TableContainer";
import { formatNumberWithCommas } from "helpers/format";
import DropdownOptionApp from "components/Common/DropdownOptionApp";


const SORT_BY_DEFAULT = 'date';
const ORDER_BY_DEFAULT = 'DESC';

registerLocale('en', en);
registerLocale('ko', ko);

const Statistic = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();
    const navigate = useNavigate();

    const APP_TYPE_OPTIONS_LANG = APP_TYPE_OPTIONS?.map(
        (item: any) => ({ value: item?.value, label: t(item?.label) })
    );

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, SORT_BY_DEFAULT),
        order_by: withDefault(StringParam, ORDER_BY_DEFAULT),
        start_date: withDefault(StringParam, `${moment().subtract(30, "days").format("Y-MM-DD")}`),
        end_date: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        app_type: withDefault(StringParam, `${APP_TYPE_OPTIONS_LANG[0]?.value}`),
    });

    const dispatch: any = useDispatch();

    const selectLayoutState = (state: any) => state.Statistic;

    const ReportProperties = createSelector(
        selectLayoutState,
        (state) => ({
            statistics: state.acquisitionStatistics,
            isStatisticSuccess: state.isAcquisitionStatisticSuccess,
            isStatisticLoading: state.isAcquisitionStatisticLoading,
            error: state.error,
        })
    );

    const { statistics, isStatisticLoading } = useSelector(ReportProperties);

    const [dateSearch, setDateSearch] = useState<any[]>([query?.start_date ? moment(query?.start_date, 'Y-MM-DD').toDate() : null, query?.end_date ? moment(query?.end_date || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateSearch;

    const [appTypeSearch, setAppTypeSearch] = useState<Option | null>(APP_TYPE_OPTIONS_LANG?.find((item) => String(item?.value) === String(query?.app_type)) || null);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        dispatch(onGetStatistics(query));
    }, [JSON.stringify(query)]);

    const searchData = () => {
        setQuery({
            ...query,
            page: 1,
            start_date: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            end_date: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            app_type: appTypeSearch?.value || '',
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            ...query,
            start_date: `${moment().subtract(30, "days").format("Y-MM-DD")}`,
            end_date: `${moment().format("Y-MM-DD")}`,
            app_type: `${APP_TYPE_OPTIONS_LANG[0]?.value}`,
            sort_by: SORT_BY_DEFAULT,
            order_by: ORDER_BY_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push");
        setAppTypeSearch(APP_TYPE_OPTIONS_LANG[0]);
        setDateSearch([moment(queryNew?.start_date || "", 'Y-MM-DD').toDate(), moment(queryNew?.end_date || "", 'Y-MM-DD').toDate()]);
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t('Date'),
                accessor: "date",
                filterable: true,
                sortable: true,
                thWidth: 150,
                thClass: 'align-middle text-start th-border-right-1px',
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const arrDate = String(cell?.value || "").split(" ");
                    const num = moment(cell?.value).days(); // 0 is Sunday , 1 is Monday, ... , 6 is Saturday
                    const classText =
                        num === 0
                            ? "text-danger row-bg-danger"
                            : num === 6
                                ? "text-primary row-bg-primary"
                                : "";
                    const textNote = t(`STANDS_${ARR_INDEX_TEXT_DAY_OF_WEEK[num]}`);
                    return !!item?.text_row ? (
                        <div className="td-border-right-1px">{item?.text_row}</div>
                    ) : (
                        <div className="text-start td-border-right-1px" style={{ minWidth: "120px" }}>
                            <span className={classText}>
                                {moment(arrDate?.[0] || "", "Y-MM-DD").format("MM-DD")} (
                                {textNote})
                            </span>
                        </div>
                    );
                },
            },
            // {
            //     Header: t('Platform'),
            //     accessor: "platform",
            //     filterable: false,
            //     sortable: false,
            //     thClass: 'align-middle text-center',
            //     thWidth: 140,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <div className="text-center">
            //                 {!!item?.text_row ? (
            //                     <strong>{item?.text_row} </strong>
            //                 ) : <> {String(cell?.value || '').toUpperCase()}</>}
            //             </div>
            //         )
            //     },
            // },
            {
                Header: t('Google'),
                accessor: "google",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                thWidth: 300,
                thComponent: () => (
                    <table className="th-border-right-1px">
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={4}>
                                    {t('Google')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Visits')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Installs')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Rates')}
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    const _platform = item?.platforms?.find((_p: any) => _p?.platform === "google");
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.visits) */}
                            <div style={{ width: '100px', borderLeft: "1px solid #eaeaea", borderRight: "1px solid #eaeaea", }}>{formatNumberWithCommas(_platform?.installs)}</div>
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.conversion_rate) + ' %' */}
                        </div>
                    );
                },
            },
            {
                Header: t('Meta'),
                accessor: "meta",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                thWidth: 300,
                thComponent: () => (
                    <table className="th-border-right-1px">
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={4}>
                                    {t('Meta')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Visits')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Installs')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Rates')}
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    const _platform = item?.platforms?.find((_p: any) => _p?.platform === "meta");
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.visits) */}
                            <div style={{ width: '100px', borderLeft: "1px solid #eaeaea", borderRight: "1px solid #eaeaea", }}>{formatNumberWithCommas(_platform?.installs)}</div>
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.conversion_rate) + ' %' */}
                        </div>
                    );
                },
            },
            {
                Header: t('Agency'),
                accessor: "agency",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                thWidth: 300,
                thComponent: () => (
                    <table className="th-border-right-1px">
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={4}>
                                    {t('Agency')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Visits')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Installs')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Rates')}
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    const _platform = item?.platforms?.find((_p: any) => _p?.platform === "agency");
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.visits) */}
                            <div style={{ width: '100px', borderLeft: "1px solid #eaeaea", borderRight: "1px solid #eaeaea", }}>{formatNumberWithCommas(_platform?.installs)}</div>
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.conversion_rate) + ' %' */}
                        </div>
                    );
                },
            },
            {
                Header: t('Shop'),
                accessor: "store",
                thClass: 'text-end',
                filterable: false,
                sortable: false,
                thWidth: 300,
                thComponent: () => (
                    <table className="th-border-right-1px">
                        <thead>
                            <tr>
                                <th className="text-center pb-1 none-sticky" colSpan={4}>
                                    {t('Shop')}
                                </th>
                            </tr>
                            <tr>
                                <tr>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Visits')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Installs')}
                                    </th>
                                    <th className="text-center px-0 py-1 cursor-pointer none-sticky" style={{ width: '100px' }}
                                    >
                                        {t('Rates')}
                                    </th>
                                </tr>
                            </tr>
                        </thead>
                    </table>
                ),
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    const _platform = item?.platforms?.find((_p: any) => _p?.platform === "store");
                    return (
                        <div className="d-flex text-center td-border-right-1px">
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.visits) */}
                            <div style={{ width: '100px', borderLeft: "1px solid #eaeaea", borderRight: "1px solid #eaeaea", }}>{formatNumberWithCommas(_platform?.installs)}</div>
                            <div style={{ width: '100px' }}>{'-'}</div>{/* formatNumberWithCommas(_platform?.conversion_rate) + ' %' */}
                        </div>
                    );
                },
            },
            {
                Header: t('Visits'),
                accessor: "visits",
                filterable: false,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 150,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <div className="text-end pe-3">
                            -
                            {/* {formatNumberWithCommas(cell?.value || 0)} */}
                        </div>
                    )
                },
            },
            {
                Header: t('Installs'),
                accessor: "installs",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 150,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <div className="text-end pe-3">
                            {formatNumberWithCommas(cell?.value || 0)}
                        </div>
                    )
                },
            },
            // {
            //     Header: t('Sign-ups'),
            //     accessor: "sign_ups",
            //     filterable: true,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     thWidth: 140,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <div className="text-end pe-3">
            //                 {formatNumberWithCommas(cell?.value || 0)}
            //             </div>
            //         )
            //     },
            // },
            {
                Header: t('Conversion Rate'),
                accessor: "conversion_rate",
                filterable: true,
                sortable: true,
                thClass: 'align-middle text-end',
                thWidth: 170,
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <div className="text-end pe-3">
                            -
                            {/* {formatNumberWithCommas(cell?.value || 0)} % */}
                        </div>
                    )
                },
            },
            // {
            //     Header: t('Total Clicks'),
            //     accessor: "total_clicks",
            //     filterable: false,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     thWidth: 200,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <div className="text-end pe-3">
            //                 {formatNumberWithCommas(cell?.value || 0)}
            //             </div>
            //         )
            //     },
            // },
            // {
            //     Header: t('Total Installs'),
            //     accessor: "total_installs",
            //     filterable: true,
            //     sortable: true,
            //     thClass: 'align-middle text-end',
            //     thWidth: 200,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <div className="text-end pe-3">
            //                 {formatNumberWithCommas(cell?.value || 0)}
            //             </div>
            //         )
            //     },
            // },
            // {
            //     Header: t('Total Conversion Rate'),
            //     accessor: "total_conversion_rate",
            //     filterable: true,
            //     sortable: true,
            //     thClass: 'align-middle text-center',
            //     thWidth: 200,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <div className="text-end pe-3">
            //             {formatNumberWithCommas(cell?.value || 0)}
            //         </div>
            //         )
            //     },
            // },
            // {
            //     Header: t('Total Sign-ups'),
            //     accessor: "total_sign_ups",
            //     filterable: true,
            //     sortable: true,
            //     thClass: 'align-middle text-center',
            //     thWidth: 200,
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>

            //             </>
            //         )
            //     },
            // },
        ],
        [i18n?.language, userPermissions]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 })
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return ({ ..._prev, ...sortBy });
        });
    }, []);

    const handleChangePicker = (values: any[] = []) => {
        setDateSearch((_prev: any) => values);
    }

    useEffect(() => {
        const handleCallAllOption = async () => {
            try {

            } catch (error: any) {
                return error;
            }
        };
        handleCallAllOption();
    }, []);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    const categories = useMemo(() => {
        return statistics?.items?.map((item: any) => item?.date)?.reverse() || [];
    }, [statistics?.items, i18n?.language]);

    const series = useMemo(() => {
        const obChart = statistics?.items?.reduce((ob: any, item: any) => {
            ob.visit?.push(0); // Number(String(item?.visits).replace(/,/g, ''))
            ob.install?.push(Number(String(item?.installs).replace(/,/g, '')));
            ob.conversion_rate?.push(0); // Number(String(item?.conversion_rate).replace(/,/g, ''))
            return ob;
        }, { visit: [], install: [], conversion_rate: [] }) || [];
        return (
            [
                {
                    name: t('Visits'),
                    type: 'column',
                    data: obChart?.visit?.reverse(),
                },
                {
                    name: t('Installs'),
                    type: 'line',
                    data: obChart?.install?.reverse(),
                },
                {
                    name: t('Conversion Rate'),
                    type: 'line',
                    data: obChart?.conversion_rate?.reverse(),
                },
            ]
        )
    }, [statistics?.items, i18n?.language]);

    useEffect(() => {
        document.title = `${t("Acquisition Statistics")} - ${t("Home")}`;
        document.body.classList.remove("vertical-sidebar-enable");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n?.language]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={t('Acquisition Statistics')} pageTitle={t('Home')} />
                    <Row>
                        <Col sm={12} md={12}>
                            <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)' }} className="ribbon-box right">
                                <div className="ribbon round-shape" style={{ background: 'rgb(40 99 213)', fontWeight: 300, fontSize: '0.8rem', top: '11px' }}>{t('This data is analyzed per 10 mins')}</div>
                                <CardHeader className="border-0" style={{ borderRadius: '30px' }}>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <div>
                                                <h5 className="card-title mb-0">
                                                    <span className="me-2">{t('Total')}:</span>
                                                    <CountUp
                                                        start={0}
                                                        end={statistics?.pagination?.total || 0}
                                                        duration={1}
                                                        className="text-primary"
                                                    />
                                                </h5>
                                            </div>
                                        </div>
                                    </Row>
                                </CardHeader>
                                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                    <Row className="g-4 align-items-center mt-0">
                                        <Col sm={6} md={3} xl={3} xxl={3} className='mt-3 mt-md-2'>
                                            <LabelWrapper label={t('App')} isShow={true}>
                                                <DropdownOptionApp
                                                    name=""
                                                    dataList={APP_TYPE_OPTIONS_LANG}
                                                    placeholder={`${t("App")}...`}
                                                    className="search-filter-category-type dropdown-status-rounded"
                                                    classNamePrefix="name-prefix"
                                                    initialValue={appTypeSearch || null}
                                                    onChangeSelect={(e: any) => setAppTypeSearch(e)}
                                                    isHasOptionAll={true}
                                                    optionAll={{ label: t('All'), value: '' }}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={6} md={3} xl={3} xxl={3} className='date-picker-wrapper-custom mt-3 mt-md-2'>
                                            <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                                <DatePickerCustom
                                                    placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                                    startDate={startDate || null}
                                                    endDate={endDate || null}
                                                    onChangePicker={handleChangePicker}
                                                    showOptions={[
                                                        'today',
                                                        'yesterday', 'two_day_ago',
                                                        'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                                        'this_week', 'last_week',
                                                        'this_month', 'last_month',
                                                    ]}
                                                    maxDate={new Date(new Date().setDate(new Date().getDate()))}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={12} md={6} xl={6} xxl={6} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-md-start mt-3 mt-md-2">
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary me-2 "
                                                    onClick={searchData}
                                                    disabled={(isStatisticLoading)}
                                                >
                                                    {t('Button Search')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary fs-14"
                                                    disabled={isLoading}
                                                    onClick={resetData}
                                                >
                                                    <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                    {t('Button Reset')}
                                                </button>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                                <ChartLine
                                    className="my-3"
                                    titles={[t('Visits'), t('Installs'), t('Conversion Rate')]}
                                    categories={categories}
                                    series={series}
                                />
                                <CardBody className="pt-3">
                                    {/* <TableAcquisition
                                        className="custom-header-css"
                                        divClass="table-responsive table-card"
                                        tableClass="align-middle table-bordered-dashed sticky-table-report-acquisition" //table-have-first-row-is-total
                                        theadClass="table-light text-muted"
                                        columns={columns}
                                        data={statistics?.items?.length ? (
                                            [
                                                ...statistics?.items]
                                        ) : []}
                                        customPageSize={query.limit}
                                        customPageIndex={query.page - 1}
                                        totalRecords={statistics?.pagination?.total}
                                        customPageCount={Math.ceil(Number(statistics?.pagination?.total) / Number(statistics?.pagination?.limit))}
                                        handleChangePage={handleChangePage}
                                        manualSorting={true}
                                        sorting={{ sort_by: query.sort_by, order_by: query.order_by }}
                                        handleChangeSorting={handleChangeSorting}
                                        isLoading={isStatisticLoading}
                                    /> */}

                                    <TableContainer
                                        className="custom-header-css"
                                        divClass="table-responsive table-card"
                                        tableClass="align-middle table-bordered-dashed table-have-first-row-is-total sticky-table-acquisition-statistics"
                                        theadClass="table-light text-muted"
                                        columns={columns}
                                        data={
                                            statistics?.items?.length
                                                ? [
                                                    {
                                                        ...statistics?.total_data,
                                                        text_row: t("Sum"),
                                                    },
                                                    ...statistics?.items,
                                                ]
                                                : []
                                        }
                                        customPageSize={query.limit}
                                        customPageIndex={query.page - 1}
                                        totalRecords={statistics?.pagination?.total}
                                        customPageCount={Math.ceil(Number(statistics?.pagination?.total) / Number(statistics?.pagination?.limit))}
                                        handleChangePage={handleChangePage}
                                        manualSorting={true}
                                        sorting={{
                                            sort_by: query.sort_by,
                                            order_by: query.order_by,
                                        }}
                                        handleChangeSorting={handleChangeSorting}
                                        isLoading={isStatisticLoading}
                                        isShowPagination={false}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <ToastContainer closeButton={false} limit={1} />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Statistic;
